import { api, apiRequest } from "./api";
import type {
  MonitoringPlanEntry,
  MonitoringPlanChange,
  MonitoringPlanChangesPage,
  UpsertMonitoringPlanEntryRequest,
  StopMonitoringPlanEntryRequest,
} from "@/types";

const base = (patientId: string) => `/monitoring/patients/${patientId}/plan/`;

export const monitoringPlanAPI = {
  // Plan active now. With `appointmentId`, a completed appointment gets the
  // plan as it was when that visit was completed (the server resolves the time).
  getPlan: (patientId: string, appointmentId?: string) =>
    apiRequest<MonitoringPlanEntry[]>(() =>
      api.get(base(patientId), {
        params: appointmentId ? { appointment_id: appointmentId } : undefined,
      })
    ),

  // Plan as it was at a point in time (ISO timestamp).
  getPlanAsOf: (patientId: string, asOf: string) =>
    apiRequest<MonitoringPlanEntry[]>(() =>
      api.get(base(patientId), { params: { as_of: asOf } })
    ),

  // Change log, newest first. Tolerates a plain array or a paginated body.
  getChanges: async (
    patientId: string,
    page: number = 1
  ): Promise<MonitoringPlanChangesPage> => {
    const data = await apiRequest<
      MonitoringPlanChangesPage | MonitoringPlanChange[]
    >(() =>
      api.get(`${base(patientId)}changes/`, {
        params: { page, page_size: 100 },
      })
    );
    if (Array.isArray(data)) {
      return { count: data.length, next: null, previous: null, results: data };
    }
    return data;
  },

  // Add or change: 201 = created/changed, 200 = identical entry already active.
  // Both are success, and apiRequest resolves for any 2xx.
  upsertEntry: (patientId: string, data: UpsertMonitoringPlanEntryRequest) =>
    apiRequest<MonitoringPlanEntry>(() => api.post(base(patientId), data)),

  stopEntry: (
    patientId: string,
    entryId: string,
    data: StopMonitoringPlanEntryRequest
  ) =>
    apiRequest<MonitoringPlanEntry>(() =>
      api.post(`${base(patientId)}${entryId}/stop/`, data)
    ),
};
